import { HttpResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PI } from '../../constants-types/PI.model';
import {
    ModuleUser,
    ModuleUserInviteRequest,
    ModuleUserInviteResponse,
    ModuleUserServer,
    ModuleUserUpdateRequest,
    InternalModuleRequest
} from '../../core-data-model/ModuleUser';
import {
    ApiService,
    FeatureFlags,
    FeatureFlagsService,
    UserProfileSettingsService
} from '../../system-services';
import { ModuleUserTransformer } from './module-user.transform';

@Injectable({ providedIn: 'root' })
export class ModuleUserApi {
    constructor(
        private api: ApiService,
        private userProfile: UserProfileSettingsService,
        private moduleUserTransformer: ModuleUserTransformer,
        private featureFlags: FeatureFlagsService
    ) {}

    async getModuleUsers(
        moduleName: string,
        excludeNoAccess = true
    ): Promise<ModuleUser[]> {
        if (
            !this.featureFlags.getByFlag(FeatureFlags.enableHideNoAccessToggle)
        ) {
            excludeNoAccess = false;
        }

        const url = `/api/v1/companies/${this.userProfile.currentCompany}/users?module=${moduleName}&excludeNoAccess=${excludeNoAccess}`;
        return this.api
            .get<ModuleUserServer[]>(url, null, null, PI.appSettings.userApiUrl)
            .then(([response, httpResponse]) => {
                if (httpResponse.status !== HttpStatusCode.Ok) {
                    return Promise.reject();
                }
                return this.moduleUserTransformer.transformServerToClient(
                    response
                );
            });
    }

    async updateUserAccess(moduleUsersUpdate: ModuleUserUpdateRequest) {
        const url = `/api/v1/companies/${this.userProfile.currentCompany}/users/batchAccessLevelChange`;
        return this.api
            .put<ModuleUserUpdateRequest>(
                url,
                moduleUsersUpdate,
                PI.appSettings.userApiUrl
            )
            .then(([responseBody, httpResponse]) => {
                if (httpResponse.status !== HttpStatusCode.Ok) {
                    return Promise.reject();
                }
                return responseBody;
            });
    }

    async updateVMAccess(moduleUsersUpdate: ModuleUserUpdateRequest) {
        const url = `/api/v1/companies/${this.userProfile.currentCompany}/users/batchVMAccessChange`;
        return this.api
            .put<ModuleUserUpdateRequest>(
                url,
                moduleUsersUpdate,
                PI.appSettings.userApiUrl
            )
            .then(([responseBody, httpResponse]) => {
                if (httpResponse.status !== HttpStatusCode.Ok) {
                    return Promise.reject();
                }
                return responseBody;
            });
    }

    async inviteUserAccess(
        moduleUserInvite: ModuleUserInviteRequest
    ): Promise<
        [ModuleUserInviteResponse, HttpResponse<ModuleUserInviteResponse>]
    > {
        const url = `/api/v1/companies/${this.userProfile.currentCompany}/users/module/inviteuser/bulk`;
        return this.api
            .post<
                ModuleUserInviteRequest,
                ModuleUserInviteResponse
            >(url, moduleUserInvite, PI.appSettings.userApiUrl)
            .then(([responseBody, httpResponse]) => {
                if (httpResponse.status !== HttpStatusCode.Ok) {
                    return Promise.reject();
                }
                return [responseBody, httpResponse];
            });
    }

    async internalActivate(internalModuleRequest: InternalModuleRequest) {
        const url = `/api/v1/companies/${this.userProfile.currentCompany}/users/module/activation`;
        return this.api
            .put<InternalModuleRequest>(
                url,
                internalModuleRequest,
                PI.appSettings.userApiUrl
            )
            .then(([responseBody, httpResponse]) => {
                if (httpResponse.status !== HttpStatusCode.Accepted) {
                    return Promise.reject();
                }
                return responseBody;
            });
    }

    async activateFreeLegacyPerform(
        internalModuleRequest: InternalModuleRequest
    ) {
        const url = `/api/v1/companies/${this.userProfile.currentCompany}/activatefreeperform`;
        return this.api
            .put<InternalModuleRequest>(
                url,
                internalModuleRequest,
                PI.appSettings.accountManagementApiUrl
            )
            .then(([responseBody, httpResponse]) => {
                if (httpResponse.status !== HttpStatusCode.Accepted) {
                    return Promise.reject();
                }
                return responseBody;
            });
    }
}
